/**
 * Canonical pure computation of a RETURN step's render options. Server-usable
 * twin of the React `flow/util.ts` copies (client-flip repoints them here).
 */
import type {
  ExchangeVariant,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { ExchangeGroup } from "@redotech/redo-return-core-common/exchange-group";
import { exchangeGroupsWhereSource } from "@redotech/redo-return-core-common/exchange-group-membership";
import { InventorySettings } from "@redotech/redo-return-core-common/inventory-settings";
import {
  ReturnOption,
  returnOptionJsonFormat,
  Step,
} from "@redotech/redo-return-core-common/return-flow";
import {
  ReturnOptionZodSchema,
  type ReturnOptionZod,
} from "@redotech/redo-return-core-common/return-flow-schema";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { StoreCreditType } from "@redotech/redo-return-core-common/return-flow/return-option-enums";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  isProductVariantInStock,
  isSameItemVariantEligible,
} from "../returns/products/product-variant-utils";

export enum ReturnOptionType {
  ADVANCED_EXCHANGE = "advanced-exchange",
  SAME_ITEM_EXCHANGE = "same-item-exchange",
  VARIANT_EXCHANGE = "variant-exchange",
  EXCHANGE_GROUPS = "exchange-groups",
  SHOP_OR_RETURN = "shop-or-return",
  BUNDLE_TYPE = "bundle-type",
}

export interface ReturnStepShopOrReturnOption {
  type: ReturnOptionType.SHOP_OR_RETURN;
  options: ReturnOption[];
}

export type ReturnStepPageOption =
  | ReturnStepShopOrReturnOption
  | {
      type: ReturnOptionType.EXCHANGE_GROUPS;
      eligibleExchangeGroups: ExchangeGroup[];
    }
  | { type: ReturnOptionType.VARIANT_EXCHANGE; returnOption: ReturnOption }
  | {
      type: ReturnOptionType.SAME_ITEM_EXCHANGE;
      variant: ExchangeVariant;
      returnOption: ReturnOption;
    }
  | { type: ReturnOptionType.BUNDLE_TYPE; options: ReturnOption[] }
  | { type: ReturnOptionType.ADVANCED_EXCHANGE };

/** Team settings slice the RETURN options computation reads. */
export interface ReturnStepOptionsSettings {
  inventory: InventorySettings;
  exchangeGroups: ExchangeGroup[];
}

/**
 * Wire-safe form of ReturnStepPageOption: ReturnOption's symbol-based method
 * type is rewritten to its plain-string zod form so it survives rpc-light.
 */
export type ReturnStepPageOptionView =
  | { type: ReturnOptionType.SHOP_OR_RETURN; options: ReturnOptionZod[] }
  | {
      type: ReturnOptionType.EXCHANGE_GROUPS;
      eligibleExchangeGroups: ExchangeGroup[];
    }
  | { type: ReturnOptionType.VARIANT_EXCHANGE; returnOption: ReturnOptionZod }
  | {
      type: ReturnOptionType.SAME_ITEM_EXCHANGE;
      variant: ExchangeVariant;
      returnOption: ReturnOptionZod;
    }
  | { type: ReturnOptionType.BUNDLE_TYPE; options: ReturnOptionZod[] }
  | { type: ReturnOptionType.ADVANCED_EXCHANGE };

function toJsonSafeReturnOption(option: ReturnOption): ReturnOptionZod {
  return ReturnOptionZodSchema.parse(returnOptionJsonFormat.write(option));
}

/**
 * Rewrite RETURN options into their plain-JSON wire form (ExchangeGroup and
 * ExchangeVariant are already plain; only ReturnOption carries symbols).
 */
export function toReturnStepPageOptionViews(
  options: ReturnStepPageOption[],
): ReturnStepPageOptionView[] {
  return options.map((option) => {
    switch (option.type) {
      case ReturnOptionType.SHOP_OR_RETURN:
      case ReturnOptionType.BUNDLE_TYPE:
        return {
          type: option.type,
          options: option.options.map(toJsonSafeReturnOption),
        };
      case ReturnOptionType.SAME_ITEM_EXCHANGE:
        return {
          type: option.type,
          variant: option.variant,
          returnOption: toJsonSafeReturnOption(option.returnOption),
        };
      case ReturnOptionType.EXCHANGE_GROUPS:
        return {
          type: option.type,
          eligibleExchangeGroups: option.eligibleExchangeGroups,
        };
      case ReturnOptionType.VARIANT_EXCHANGE:
        return {
          type: option.type,
          returnOption: toJsonSafeReturnOption(option.returnOption),
        };
      case ReturnOptionType.ADVANCED_EXCHANGE:
        return { type: option.type };
    }
  });
}

function getRefundReturnOptions(returnStep: Step.Return): ReturnOption[] {
  return returnStep.options.filter(
    (option) =>
      option.method.type !== ReturnOptionMethod.EXCHANGE ||
      option.exchangeTypes.advanced,
  );
}

/**
 * True when the return step offers only same-item/variant exchange with no
 * non-exchange escape, so declining would silently fall through to a refund.
 */
export function returnStepRequiresExchange(
  returnStep: Pick<Step.Return, "options">,
): boolean {
  return (
    returnStep.options.length > 0 &&
    returnStep.options.every(
      (option) =>
        option.method.type === ReturnOptionMethod.EXCHANGE &&
        !option.exchangeTypes.advanced,
    )
  );
}

function getSameItemVariant(
  product: ReturnProduct,
  settings: ReturnStepOptionsSettings,
  returnType: ReturnTypeEnum | undefined,
): ExchangeVariant | undefined {
  return product.variantData.variants.find(
    (variant) =>
      String(variant.id) === product.variantId &&
      isSameItemVariantEligible({
        inventorySettings: settings.inventory,
        returnType,
        productStatus: product.variantData.status,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryTracked: variant.inventoryTracked,
        inventoryQuantity: variant.inventoryQuantity,
      }),
  );
}

export function getEligibleExchangeGroupsForProduct(
  product: ReturnProduct,
  exchangeGroups: ExchangeGroup[] | undefined,
): ExchangeGroup[] {
  // The flow has always matched a collection group by name; ids are what the
  // storefront lookup gives the engine. Passing none keeps the flow's answer.
  return exchangeGroupsWhereSource(
    {
      title: product.variantData.productTitle,
      tags: product.variantData.tags,
      collectionIds: [],
      collectionNames: product.collections,
    },
    exchangeGroups,
  );
}

export function getReturnStepPageOptions(
  returnStep: Step.Return,
  product: ReturnProduct,
  settings: ReturnStepOptionsSettings,
  returnableItem: ReturnableItem,
  returnType: ReturnTypeEnum | undefined,
): ReturnStepPageOption[] {
  if (returnableItem?.isExpanded && returnableItem.products.length > 1) {
    return [
      { type: ReturnOptionType.BUNDLE_TYPE, options: returnStep.options },
    ];
  }

  const options: ReturnStepPageOption[] = [];
  const exchangeOptions = returnStep.options.filter(
    (option) => option.method.type === ReturnOptionMethod.EXCHANGE,
  );
  const sameItemExchangeOption = exchangeOptions.find(
    (option) => option.exchangeTypes.sameItem,
  );
  const sameItemVariant =
    sameItemExchangeOption && getSameItemVariant(product, settings, returnType);

  if (sameItemVariant) {
    options.push({
      type: ReturnOptionType.SAME_ITEM_EXCHANGE,
      variant: sameItemVariant,
      returnOption: sameItemExchangeOption,
    });
  }

  const canDoVariantExchange =
    exchangeOptions.some((option) => option.exchangeTypes.variant) &&
    product.variantData.variants.some(
      (variant) =>
        String(variant.id) !== product.variantId &&
        isProductVariantInStock({
          inventorySettings: settings.inventory,
          returnType,
          productStatus: product.variantData.status,
          inventoryPolicy: variant.inventoryPolicy,
          inventoryTracked: variant.inventoryTracked,
          inventoryQuantity: variant.inventoryQuantity,
        }),
    );

  // Mirror the client's getVariantExchangeReturnOption: the underlying exchange
  // option the shopper's chosen variant is finalized against (add-on + preserve).
  const variantExchangeReturnOption = exchangeOptions.find(
    (option) => option.exchangeTypes.variant || option.exchangeTypes.sameItem,
  );
  if (canDoVariantExchange && variantExchangeReturnOption) {
    options.push({
      type: ReturnOptionType.VARIANT_EXCHANGE,
      returnOption: variantExchangeReturnOption,
    });
  }

  const exchangeGroupsAllowed = exchangeOptions.some(
    (option) => option.exchangeTypes.exchangeGroups,
  );
  if (exchangeGroupsAllowed) {
    const eligibleExchangeGroups = getEligibleExchangeGroupsForProduct(
      product,
      settings.exchangeGroups,
    );
    if (eligibleExchangeGroups.length > 0) {
      options.push({
        type: ReturnOptionType.EXCHANGE_GROUPS,
        eligibleExchangeGroups,
      });
    }
  }

  const advancedExchangeOption = exchangeOptions.find(
    (option) => option.exchangeTypes.advanced,
  );
  if (advancedExchangeOption) {
    options.push({ type: ReturnOptionType.ADVANCED_EXCHANGE });
  }

  const otherReturnOptions = getRefundReturnOptions(returnStep);
  if (otherReturnOptions.length) {
    options.push({
      type: ReturnOptionType.SHOP_OR_RETURN,
      options: otherReturnOptions,
    });
  }

  if (options.length === 0 && returnStep.options[0]) {
    // All exchange options out of stock or the flow is misconfigured: fall back
    // to a store-credit return so the shopper is never left with no option.
    options.push({
      type: ReturnOptionType.SHOP_OR_RETURN,
      options: [
        {
          ...returnStep.options[0],
          method: {
            type: ReturnOptionMethod.STORE_CREDIT,
            creditType: StoreCreditType.GIFT_CARD,
          },
        },
      ],
    });
  }

  return options;
}
